<template>
  <div class="google-login-container">
    <div id="google-signin-button"></div>
    <Loader2 v-if="isLoading" class="size-4 animate-spin mt-3" />
    <div v-if="error" class="error text-destructive font-medium text-sm mt-3">{{ error }}</div>
  </div>
</template>

<script lang="ts">
import { AuthService } from '@/services/auth.ts'
import { DashboardFilters } from '@/services/dashboard-filters.ts'
import {AuthApi} from "@/services/api/auth.ts";
import { CurrentUserService } from '@/services/current-user'
import { useToast } from '@/components/ui/toast/use-toast'
import {Loader2} from "lucide-vue-next";
const { toast } = useToast()

export default {
  name: 'GoogleLogin',
  components: {Loader2},
  data() {
    return {
      isLoading: false,
      error: null
    };
  },
  mounted() {
    this.loadGoogleScript();
  },
  methods: {
    loadGoogleScript() {
      const script = document.createElement('script');
      script.src = 'https://accounts.google.com/gsi/client';
      script.async = true;
      script.defer = true;
      script.onload = this.initGoogleButton;
      document.head.appendChild(script);
    },
    initGoogleButton() {
      if (window.google) {
        window.google.accounts.id.initialize({
          client_id: import.meta.env.VITE_SSO_GOOGLE_CLIENT_ID,
          callback: this.handleGoogleResponse
        });

        window.google.accounts.id.renderButton(
          document.getElementById('google-signin-button'),
          { theme: 'outline', size: 'large', locale: 'en', width: '324px', logo_alignment: 'center' }
        );

        // Arriving from a link that asked for Google (?sso=google): open the
        // Google account prompt right away instead of waiting for a click
        if (this.$route.query.sso === 'google') {
          window.google.accounts.id.prompt();
        }
      }
    },
    async handleGoogleResponse(response) {
      this.isLoading = true;
      this.error = null;

      try {
        const tokens = await AuthApi.googleLogin({
          token: response.credential
        })

        AuthService.shouldRemember(true)
        AuthService.setTokens(tokens.access, tokens.refresh)
        CurrentUserService.setCurrentUser(tokens.user)
        // Use the new method that ensures currentRole is always updated
        await DashboardFilters.initializeRolesAfterLogin(tokens.roles)

        // Notify parent component or use Vuex to update app state
        this.$emit('login-success', tokens);

        // Redirect to intended destination or dashboard
        this.$router.push(AuthService.consumeRedirectUrl());
      } catch (error) {
        this.error = error.response?.data?.message || 'Google Sign-In Failed';

        toast({
          title: 'Google Sign-In Failed',
          description: 'An unexpected error occurred. Please try again.',
          variant: 'destructive',
        });

        console.error('Google login error:', error);
      } finally {
        this.isLoading = false;
      }
    }
  }
};
</script>

<style scoped>
.google-login-container {
  display: flex;
  flex-direction: column;
  align-items: center;
}
</style>
